/**
 * Resource Item Utilities
 *
 * Helpers for items of the `structured_response` payload. Every item carries a
 * normalized profile (`item.profile`) shaped by one of the API normalized types:
 * listing, event, page, article or offer.
 */

/** Normalized resource types known by the API (see the API `normalized_types`). */
export const RESOURCE_TYPES = ['listing', 'event', 'page', 'article', 'offer'];

/**
 * Resolves the resource type used to pick the card component of an item.
 *
 * `__writer_type` names the normalized type the profile was written as (e.g. a
 * crawled page holding an event profile), so it wins over the stored resource type
 * because the card must match the shape of the profile.
 * Unknown types are kept (they render with the default card); a missing type
 * falls back to 'listing'.
 *
 * @param {Object} item - The structured response item.
 * @returns {string} Lowercase resource type.
 */
export function resolveItemType(item) {
  const prof = item?.profile || {};
  const candidates = [prof.__writer_type, item?.__writer_type, item?.type, prof.type]
    .filter((value) => typeof value === 'string' && value.trim())
    .map((value) => value.trim().toLowerCase());

  return candidates.find((type) => RESOURCE_TYPES.includes(type)) || candidates[0] || 'listing';
}

/**
 * Checks whether an item has a valid latitude/longitude pair (on the item or its profile).
 *
 * @param {Object} item - The structured response item or map point.
 * @returns {boolean}
 */
export function hasCoordinates(item) {
  const prof = item?.profile || {};
  const isCoordinate = (value) => value !== null && value !== undefined && value !== '' && isFinite(Number(value));
  return isCoordinate(item?.latitude ?? prof.latitude) && isCoordinate(item?.longitude ?? prof.longitude);
}
